/**
 * A small generic offline outbox: durable IndexedDB storage for a write a caller wants to
 * "send now if possible, and if not, queue it and never lose it." `captureQueue.ts` (§8.1)
 * and `taskQueue.ts` (§8.2) are its two instantiations — this was factored out of
 * `captureQueue.ts` once a second, genuinely different payload shape needed the exact same
 * open/put/delete/getAll-and-single-flight-flush machinery, not written generic up front.
 *
 * Every instance gets its own IndexedDB database (one object store each) so that a capture
 * and a queued task creation can never collide on id even though both mint from the same
 * `newUlid()`.
 */

export type OutboxRecord = {
  idempotencyKey: string;
  /** Sort key for flush order — best-effort "oldest first", not a guarantee. Millisecond
   * resolution means two records queued in the same tick can tie, and §8.1's own test suite
   * (`test_captures_survive_out_of_order_delivery`) already treats exact order as not load
   * bearing: nothing here depends on it, so a tie just flushes in whatever order `getAll`
   * returns it, which is fine. See DECISIONS.md D156 — an earlier version added a monotonic
   * counter to break ties exactly; removed as solving a problem the spec never had. */
  queuedAt: number;
  /** Diagnostic only — an outbox never gives up on a record on its own (§0: nothing captured
   * or queued is ever lost), so nothing reads this to decide whether to keep retrying. */
  attempts: number;
  lastError: string | null;
  /** Epoch ms before which this record is not attempted, even though it is already durably
   * queued (§8.1). Exists for a wait that has nothing to do with connectivity — a capture
   * held back a few seconds for a precise GPS fix to arrive, say — reusing the same
   * durability the network-failure case already has rather than inventing a second
   * "temporarily can't send" mechanism next to it. Absent or in the past means "always
   * eligible", so every existing record and every non-timed caller is unaffected. */
  readyAt?: number;
};

export type FlushResult = { flushed: number; remaining: number; givenUp: number };

/** How often to retry while something is waiting to send. Slow on purpose: the fast paths
 * (`online`, focus, becoming visible) already cover every way a reader actively comes back,
 * so this only has to catch connectivity returning under an app nobody is touching. */
const RETRY_INTERVAL_MS = 30_000;

// -------------------------------------------------------------------------------------------
// ULID (§5.1) — hand-rolled rather than a dependency; the format is short enough that every
// npm alternative pulls in more than this needs.
// -------------------------------------------------------------------------------------------

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

function encodeBase32(value: number, length: number): string {
  let out = "";
  let remaining = value;
  for (let i = 0; i < length; i++) {
    out = CROCKFORD[remaining % 32] + out;
    remaining = Math.floor(remaining / 32);
  }
  return out;
}

/**
 * A 26-character Crockford-base32 ULID: a 48-bit millisecond timestamp (10 chars) followed by
 * 80 bits of randomness (16 chars) — the same shape as `backend/efss/ids.py`'s `new_id()`, so
 * a client-minted key is indistinguishable from a server-minted one.
 */
export function newUlid(): string {
  const time = encodeBase32(Date.now(), 10);
  const randomBytes = new Uint8Array(16);
  crypto.getRandomValues(randomBytes);
  let random = "";
  for (const byte of randomBytes) random += CROCKFORD[byte % 32];
  return time + random;
}

/** The bookkeeping fields every outbox record needs, ready to spread into a payload-specific
 * record: `{ ...outboxRecordBase(), text: trimmed }`. */
export function outboxRecordBase(): OutboxRecord {
  return {
    idempotencyKey: newUlid(),
    queuedAt: Date.now(),
    attempts: 0,
    lastError: null,
  };
}

// -------------------------------------------------------------------------------------------
// IndexedDB plumbing — three small, independent operations rather than one shared
// "run a transaction" helper; each is a single request against a single store, and factoring
// that further would hide more than three near-identical blocks are worth hiding.
// -------------------------------------------------------------------------------------------

function openDb(dbName: string, storeName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const openRequest = indexedDB.open(dbName, 1);
    openRequest.onupgradeneeded = () => {
      openRequest.result.createObjectStore(storeName, { keyPath: "idempotencyKey" });
    };
    openRequest.onsuccess = () => resolve(openRequest.result);
    openRequest.onerror = () =>
      reject(openRequest.error ?? new Error(`could not open ${dbName}`));
  });
}

async function putRecord<T extends OutboxRecord>(
  dbName: string,
  storeName: string,
  record: T,
): Promise<void> {
  const db = await openDb(dbName, storeName);
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite");
      tx.objectStore(storeName).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error(`could not queue to ${dbName}`));
    });
  } finally {
    db.close();
  }
}

async function deleteRecord(dbName: string, storeName: string, idempotencyKey: string): Promise<void> {
  const db = await openDb(dbName, storeName);
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite");
      tx.objectStore(storeName).delete(idempotencyKey);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error(`could not clear a flushed record in ${dbName}`));
    });
  } finally {
    db.close();
  }
}

async function getAllRecords<T extends OutboxRecord>(dbName: string, storeName: string): Promise<T[]> {
  const db = await openDb(dbName, storeName);
  try {
    return await new Promise<T[]>((resolve, reject) => {
      const tx = db.transaction(storeName, "readonly");
      const request = tx.objectStore(storeName).getAll();
      request.onsuccess = () => resolve(request.result as T[]);
      request.onerror = () => reject(request.error ?? new Error(`could not read ${dbName}`));
    });
  } finally {
    db.close();
  }
}

async function getRecord<T extends OutboxRecord>(
  dbName: string,
  storeName: string,
  idempotencyKey: string,
): Promise<T | undefined> {
  const db = await openDb(dbName, storeName);
  try {
    return await new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction(storeName, "readonly");
      const request = tx.objectStore(storeName).get(idempotencyKey);
      request.onsuccess = () => resolve(request.result as T | undefined);
      request.onerror = () => reject(request.error ?? new Error(`could not read a record from ${dbName}`));
    });
  } finally {
    db.close();
  }
}

// -------------------------------------------------------------------------------------------
// The outbox itself
// -------------------------------------------------------------------------------------------

export type Outbox<T extends OutboxRecord> = {
  /** Persist a record and return once it's durable; a flush is fired in the background,
   * deliberately not awaited by the caller — a slow or absent connection must never make a
   * write feel like it's hanging. */
  enqueue: (record: T) => Promise<void>;
  /** Attempt every queued record, oldest first. One record's failure does not block the ones
   * behind it. Single-flighted (see the module doc) — a concurrent call joins whichever pass
   * is already running instead of starting a second one. */
  flush: () => Promise<FlushResult>;
  pendingCount: () => Promise<number>;
  /** The one record named by this key, or `undefined` if it has already been sent (or was
   * never queued). Read-then-`enqueue()` is how a caller amends an already-queued record —
   * attaching a location that arrived after the record was written, say — since `enqueue`'s
   * `put` semantics upsert on `idempotencyKey` rather than reject a repeat. */
  get: (idempotencyKey: string) => Promise<T | undefined>;
  /** Wires the outbox into the app's lifetime: flush once when called (covers records queued
   * last session that never got a reconnect before the tab closed), and again whenever the
   * app is plausibly reachable once more — see the trigger list in the implementation, which
   * deliberately does not rely on `online` alone. Returns an unsubscribe function. */
  startFlushing: () => () => void;
};

export function createOutbox<T extends OutboxRecord>(config: {
  /** A dedicated IndexedDB database per outbox — never shared between instances, so two kinds
   * of queued write can never collide even though both mint ids from the same `newUlid()`. */
  dbName: string;
  storeName: string;
  /** How to actually deliver one record. Throwing (a network failure, a non-2xx response)
   * leaves the record queued for the next flush, unless `shouldRetry` says otherwise. */
  send: (record: T) => Promise<unknown>;
  /**
   * Whether a failed `send` should stay queued for the next flush. Defaults to always
   * retrying, which is the only safe default here: a `send` can fail because the endpoint
   * doesn't exist *yet* (§8.1's capture queue was built before `/api/capture` was — D151),
   * and that failure looks identical, from here, to one that will never
   * succeed. An outbox whose endpoints are all already built and stable can instead give up
   * on a failure that reached the server and was refused for a reason retrying can't fix
   * (e.g. `ApiError` that isn't `isOffline`) — see `taskQueue.ts`, DECISIONS.md D157.
   */
  shouldRetry?: (error: unknown) => boolean;
  /** Called once per record given up on (`shouldRetry` returned `false`), after it has
   * already been removed from the queue — the only chance for the caller to tell the reader
   * what happened to it, since it will not be retried. */
  onGiveUp?: (record: T, error: unknown) => void;
  /** Called after any flush pass that actually delivered at least one record, however it was
   * triggered. Exists because a flush usually completes *after* the screen has already
   * rendered — on app start, or on reconnect — so without this the work lands on the server
   * and the reader sees no sign of it until they reload again by hand. */
  onDelivered?: (result: FlushResult) => void;
}): Outbox<T> {
  const { dbName, storeName, send, onGiveUp, onDelivered } = config;
  const shouldRetry = config.shouldRetry ?? (() => true);

  let flushInFlight: Promise<FlushResult> | null = null;
  /**
   * Whether anything is believed to be waiting, so the retry heartbeat can do nothing at all
   * in the overwhelmingly common case of an empty queue rather than opening IndexedDB every
   * few seconds forever.
   *
   * Starts `true`, deliberately: at construction nothing has read the database yet, and a
   * queue left full by the previous session is exactly what the first flush is for. It is
   * corrected to the truth by the end of that flush.
   */
  let pending = true;

  function flush(): Promise<FlushResult> {
    if (!flushInFlight) {
      flushInFlight = runFlush().finally(() => {
        flushInFlight = null;
      });
    }
    return flushInFlight;
  }

  async function runFlush(): Promise<FlushResult> {
    const records = (await getAllRecords<T>(dbName, storeName)).sort(
      (a, b) => a.queuedAt - b.queuedAt,
    );
    let flushed = 0;
    let givenUp = 0;
    for (const record of records) {
      if (record.readyAt && record.readyAt > Date.now()) {
        // Not a failure and not an attempt — a record deliberately held back for a reason
        // that has nothing to do with whether the server is reachable, so it costs nothing
        // towards `attempts` and never reaches `send`. Still counted in `remaining` below,
        // which is what keeps `pending` true so the heartbeat eventually reaches it even if
        // whatever set `readyAt` never explicitly asks for a flush again.
        continue;
      }
      try {
        await send(record);
        await deleteRecord(dbName, storeName, record.idempotencyKey);
        flushed++;
      } catch (err) {
        if (!shouldRetry(err)) {
          await deleteRecord(dbName, storeName, record.idempotencyKey);
          givenUp++;
          onGiveUp?.(record, err);
          continue;
        }
        await putRecord(dbName, storeName, {
          ...record,
          attempts: record.attempts + 1,
          lastError: err instanceof Error ? err.message : String(err),
        });
      }
    }
    const result = { flushed, remaining: records.length - flushed - givenUp, givenUp };
    pending = result.remaining > 0;
    if (flushed > 0) onDelivered?.(result);
    return result;
  }

  async function enqueue(record: T): Promise<void> {
    await putRecord(dbName, storeName, record);
    pending = true;
    void flush();
  }

  async function pendingCount(): Promise<number> {
    return (await getAllRecords<T>(dbName, storeName)).length;
  }

  async function get(idempotencyKey: string): Promise<T | undefined> {
    return getRecord<T>(dbName, storeName, idempotencyKey);
  }

  function startFlushing(): () => void {
    const handler = () => void flush();

    // `online` alone is not enough, and assuming it was cost real queued work (D162).
    // `navigator.onLine` tracks whether the *browser* has a network interface, not whether
    // EFSS is reachable over it — so it fires for airplane mode, and never fires for the
    // cases this app actually meets: a VPN toggled off and on, the server restarting, wifi
    // that is connected but not routing. Each of the triggers below covers a way back that
    // `online` misses:
    window.addEventListener("online", handler);
    // Returning to the app — switching back to the tab, or reopening the installed PWA,
    // which is what a phone does after the reader has been elsewhere.
    window.addEventListener("focus", handler);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") handler();
    });
    // And a slow heartbeat for the case with no signal at all: the app left open and visible
    // while connectivity returns underneath it. Cheap, because it skips the database entirely
    // unless something is actually waiting — see `pending` below.
    const timer = window.setInterval(() => {
      if (pending) handler();
    }, RETRY_INTERVAL_MS);

    handler();
    return () => {
      window.removeEventListener("online", handler);
      window.removeEventListener("focus", handler);
      window.clearInterval(timer);
    };
  }

  return { enqueue, flush, pendingCount, get, startFlushing };
}
